<div class="lp">
  <!-- ── Navigation ─────────────────────────────────────────────── -->
  <header class="lp-nav" [class.is-open]="menuOpen()">
    <div class="lp-container lp-nav__inner">
      <a routerLink="/" class="lp-brand" aria-label="PulseWatch home">
        <span class="lp-brand__mark" aria-hidden="true">
          <svg viewBox="0 0 64 64"><path d="M9 35h11l5-12 8 24 6-17 3 5h13" /></svg>
        </span>
        <span class="lp-brand__name">PulseWatch</span>
      </a>

      <nav class="lp-nav__links" aria-label="Main">
        <a (click)="scrollTo('features')">Features</a>
        <a (click)="scrollTo('how')">How it works</a>
        <a (click)="scrollTo('pricing')">Pricing</a>
        <a routerLink="/knowledge-base">Docs</a>
      </nav>

      <div class="lp-nav__actions">
        <a routerLink="/login" class="lp-link">Log in</a>
        <a routerLink="/register" class="lp-btn lp-btn--primary lp-btn--sm">Get started</a>
      </div>

      <button type="button" class="lp-nav__burger" (click)="toggleMenu()" [attr.aria-expanded]="menuOpen()" aria-label="Menu">
        <i class="bx" [class.bx-menu]="!menuOpen()" [class.bx-x]="menuOpen()"></i>
      </button>
    </div>

    @if (menuOpen()) {
      <nav class="lp-nav__drawer" aria-label="Mobile">
        <a (click)="scrollTo('features')">Features</a>
        <a (click)="scrollTo('how')">How it works</a>
        <a (click)="scrollTo('pricing')">Pricing</a>
        <a routerLink="/knowledge-base">Docs</a>
        <a routerLink="/login">Log in</a>
        <a routerLink="/register" class="lp-btn lp-btn--primary">Get started</a>
      </nav>
    }
  </header>

  <!-- ── Hero ───────────────────────────────────────────────────── -->
  <section class="lp-hero">
    <div class="lp-hero__glow" aria-hidden="true"></div>
    <div class="lp-container lp-hero__inner">
      <div class="lp-hero__copy">
        <span class="lp-eyebrow"><span class="lp-dot"></span> Status pages · Uptime · Errors · Tracing</span>
        <h1>Know it's down <span class="lp-gradient-text">before your users do.</span></h1>
        <p class="lp-hero__lead">
          PulseWatch checks your services around the clock, publishes a status page your users can trust,
          and catches the errors your apps throw — with the request that caused them.
        </p>
        <div class="lp-hero__ctas">
          <a routerLink="/register" class="lp-btn lp-btn--primary">Start monitoring <i class="bx bx-right-arrow-alt"></i></a>
          <a routerLink="/knowledge-base" class="lp-btn lp-btn--ghost">Read the docs</a>
        </div>
        <ul class="lp-hero__facts">
          <li><i class="bx bx-check"></i> Checks every 30 seconds</li>
          <li><i class="bx bx-check"></i> Angular & Django SDKs</li>
          <li><i class="bx bx-check"></i> Swiss made</li>
        </ul>
      </div>

      <div class="lp-hero__visual">
        <app-pulse-globe></app-pulse-globe>

        <div class="lp-chip lp-chip--status" aria-hidden="true">
          <span class="lp-chip__led lp-chip__led--ok"></span>
          <div><strong>API</strong><small>Operational · 99.98%</small></div>
        </div>
        <div class="lp-chip lp-chip--issue" aria-hidden="true">
          <i class="bx bx-bug"></i>
          <div><strong>TypeError</strong><small>checkout.ts:42 · 3 users</small></div>
        </div>
        <div class="lp-chip lp-chip--trace" aria-hidden="true">
          <i class="bx bx-line-chart"></i>
          <div><strong>POST /orders</strong><small>182 ms · 14 queries</small></div>
        </div>
      </div>
    </div>
  </section>

  <!-- ── Features ───────────────────────────────────────────────── -->
  <section class="lp-section" id="features">
    <div class="lp-container">
      <header class="lp-section__head">
        <span class="lp-kicker">Features</span>
        <h2>One project. Everything you need to stay up.</h2>
        <p>Uptime, status communication and application errors live side by side, so an incident is one click from its cause.</p>
      </header>

      <div class="lp-bento">
        @for (f of features; track f.title) {
          <a class="lp-card lp-feature" [class.lp-feature--wide]="f.wide" [routerLink]="f.link">
            <span class="lp-feature__icon"><i class="bx {{ f.icon }}"></i></span>
            <h3>{{ f.title }}</h3>
            <p>{{ f.text }}</p>
            <span class="lp-feature__more">Learn more <i class="bx bx-right-arrow-alt"></i></span>
          </a>
        }
      </div>
    </div>
  </section>

  <!-- ── Status page preview ───────────────────────────────────── -->
  <section class="lp-section lp-section--tight">
    <div class="lp-container lp-split">
      <div class="lp-split__copy">
        <span class="lp-kicker">Status pages</span>
        <h2>Transparency your users can bookmark.</h2>
        <p>
          Every project gets a public page at <code>/status/your-product</code>. Components turn red on outages,
          blue during announced maintenance, and planned work never counts against your uptime.
        </p>
        <a routerLink="/knowledge-base/status-pages/public-status-page" class="lp-link lp-link--arrow">
          How status pages work <i class="bx bx-right-arrow-alt"></i>
        </a>
      </div>

      <div class="lp-card lp-status" aria-label="Example status page">
        <div class="lp-status__banner"><i class="bx bxs-check-circle"></i> All systems operational</div>
        @for (name of ['Website', 'API', 'Checkout']; track name; let row = $index) {
          <div class="lp-status__row">
            <div class="lp-status__meta">
              <span>{{ name }}</span>
              <small>{{ row === 1 ? '99.84' : '100.0' }}% uptime</small>
            </div>
            <div class="lp-status__bars">
              @for (day of previewDays; track $index) {
                <span class="lp-status__bar lp-status__bar--{{ row === 1 ? day : day === 'down' ? 'ok' : day }}"></span>
              }
            </div>
          </div>
        }
        <div class="lp-status__legend"><span>60 days ago</span><span>Today</span></div>
      </div>
    </div>
  </section>

  <!-- ── How it works ──────────────────────────────────────────── -->
  <section class="lp-section" id="how">
    <div class="lp-container">
      <header class="lp-section__head">
        <span class="lp-kicker">How it works</span>
        <h2>Live in minutes, not sprints.</h2>
      </header>

      <div class="lp-steps">
        <div class="lp-card lp-step">
          <span class="lp-step__num">1</span>
          <h3>Create a project</h3>
          <p>Pick a name and the address of your public status page.</p>
        </div>
        <div class="lp-card lp-step">
          <span class="lp-step__num">2</span>
          <h3>Add what to watch</h3>
          <p>Point checks at your health endpoints and choose how often they run.</p>
        </div>
        <div class="lp-card lp-step">
          <span class="lp-step__num">3</span>
          <h3>Drop in the SDK</h3>
          <p>Errors and traces from your Angular or Django app start flowing in.</p>
        </div>
      </div>

      <div class="lp-code lp-card" aria-label="Django SDK configuration example">
        <div class="lp-code__bar"><span></span><span></span><span></span><em>settings.py</em></div>
        <pre><code><span class="c-k">PULSEWATCH</span> = &#123;
    <span class="c-s">"ingest_url"</span>: <span class="c-s">"https://pulsewatch-api…/ingest/&lt;public_key&gt;"</span>,
    <span class="c-s">"environment"</span>: <span class="c-s">"production"</span>,
    <span class="c-s">"traces_sample_rate"</span>: <span class="c-n">0.2</span>,
&#125;

<span class="c-k">MIDDLEWARE</span> = [<span class="c-s">"pulsewatch_sdk.middleware.PulseWatchMiddleware"</span>, …]</code></pre>
      </div>
    </div>
  </section>

  <!-- ── Pricing ───────────────────────────────────────────────── -->
  <section class="lp-section" id="pricing">
    <div class="lp-container">
      <header class="lp-section__head">
        <span class="lp-kicker">Pricing</span>
        <h2>Simple, transparent pricing.</h2>
        <p>Pick a plan that fits today and switch whenever you grow.</p>
      </header>

      <div class="lp-plans">
        @for (plan of plans; track plan.name) {
          <div class="lp-card lp-plan" [class.lp-plan--featured]="plan.featured">
            @if (plan.featured) {
              <span class="lp-plan__badge">Most popular</span>
            }
            <h3>{{ plan.name }}</h3>
            <p class="lp-plan__tagline">{{ plan.tagline }}</p>
            @if (plan.price === 0) {
              <p class="lp-plan__price">Free</p>
            } @else {
              <p class="lp-plan__price"><span>CHF</span>{{ plan.price }}<small>/month</small></p>
            }
            <ul>
              @for (feature of plan.features; track feature) {
                <li><i class="bx bx-check"></i>{{ feature }}</li>
              }
            </ul>
            <a routerLink="/register" class="lp-btn" [class.lp-btn--primary]="plan.featured" [class.lp-btn--ghost]="!plan.featured">
              Get started
            </a>
          </div>
        }
      </div>
    </div>
  </section>

  <!-- ── Newsletter CTA ────────────────────────────────────────── -->
  <section class="lp-section lp-section--tight">
    <div class="lp-container">
      <div class="lp-cta">
        <div class="lp-cta__copy">
          <h2>Stay in the loop.</h2>
          <p>Product updates and new features, a few times a year. No spam.</p>
        </div>

        @if (contactEmailSuccess()) {
          <p class="lp-cta__success"><i class="bx bxs-check-circle"></i> You're in! We'll keep you posted.</p>
        } @else {
          <form class="lp-cta__form" (ngSubmit)="submitContactEmail()" novalidate>
            <input
              type="email"
              name="email"
              placeholder="you@company.com"
              aria-label="Email address"
              [(ngModel)]="contactEmail"
              [class.is-invalid]="contactEmailError()"
            />
            <button type="submit" class="lp-btn lp-btn--light" [disabled]="contactSending()">
              {{ contactSending() ? 'Subscribing…' : 'Subscribe' }}
            </button>
            @if (contactEmailError()) {
              <p class="lp-cta__error">{{ contactEmailError() }}</p>
            }
          </form>
        }
      </div>
    </div>
  </section>

  <!-- ── Footer ────────────────────────────────────────────────── -->
  <footer class="lp-footer">
    <div class="lp-container lp-footer__inner">
      <a routerLink="/" class="lp-brand">
        <span class="lp-brand__mark" aria-hidden="true">
          <svg viewBox="0 0 64 64"><path d="M9 35h11l5-12 8 24 6-17 3 5h13" /></svg>
        </span>
        <span class="lp-brand__name">PulseWatch</span>
      </a>
      <nav class="lp-footer__links" aria-label="Footer">
        <a routerLink="/knowledge-base">Knowledge base</a>
        <a [href]="apiDocsUrl" target="_blank" rel="noopener">API docs</a>
        <a href="https://app.finitopro.ch/portal/0149137a-24cd-4fe3-8562-aaac1d79d217" target="_blank" rel="noopener" data-testid="landing-contact">Contact</a>
        <a routerLink="/login">Log in</a>
        <a routerLink="/register">Register</a>
      </nav>
      <p class="lp-footer__meta">
        &#x1F1E8;&#x1F1ED; Swiss made software · © {{ year }} SM Tech GmbH ·
        <a routerLink="/imprint" data-testid="landing-imprint">Imprint</a> ·
        <a routerLink="/terms" data-testid="landing-terms">Terms</a> ·
        <a routerLink="/privacy" data-testid="landing-privacy">Privacy</a>
      </p>
    </div>
  </footer>
</div>
